import { sanitizeMjcfName } from "../utils/mjcfNames";

/** @typedef {Record<string, any>} JointMap */
/** @typedef {Record<string, any>} ActuatorMap */

function resolveJointUuidForActuator(actuator, joints, byName, bySanitizedName) {
  const jointRef = actuator?.joint;
  if (!jointRef) return null;

  if (typeof jointRef === "string") {
    if (joints[jointRef]) return jointRef;
    const byExactName = byName.get(jointRef);
    if (byExactName) return byExactName;
    const bySanitized = bySanitizedName.get(sanitizeMjcfName(jointRef));
    if (bySanitized) return bySanitized;
    return null;
  }

  const objectRef = jointRef && typeof jointRef === "object" ? jointRef : null;
  const uuid = objectRef?.uuid;
  const name = objectRef?.name;
  if (uuid && joints[uuid]) return uuid;
  if (name && byName.has(name)) return byName.get(name);
  return null;
}

/**
 * Build sets/maps used by joint actuator visualization — single source for
 * "which joints are actuated" and display strings (comma-separated actuator names).
 * @param {ActuatorMap | null | undefined} actuators
 * @param {JointMap | null | undefined} joints
 */
export function indexActuatorsForJointVisualization(actuators, joints) {
  const jointsRecord = joints && typeof joints === "object" ? joints : {};

  const byName = new Map();
  const bySanitizedName = new Map();
  Object.entries(jointsRecord).forEach(([jointUuid, joint]) => {
    if (!joint?.name) return;
    const name = String(joint.name);
    byName.set(name, jointUuid);
    bySanitizedName.set(sanitizeMjcfName(name), jointUuid);
  });

  const actuatedJointUuids = new Set();
  /** @type {Map<string, string[]>} */
  const namesAcc = new Map();

  Object.values(actuators || {}).forEach((actuator) => {
    const jointUuid = resolveJointUuidForActuator(actuator, jointsRecord, byName, bySanitizedName);
    if (!jointUuid) return;

    actuatedJointUuids.add(jointUuid);
    const nm = actuator?.name != null ? String(actuator.name).trim() : "";
    const fallbackUuid = actuator?.uuid != null ? String(actuator.uuid).trim() : "";
    const label = nm || fallbackUuid || "";
    if (!namesAcc.has(jointUuid)) namesAcc.set(jointUuid, []);
    if (label) namesAcc.get(jointUuid).push(label);
  });

  /** @type {Map<string, string>} */
  const actuatorDisplayLabelByJointUuid = new Map();
  namesAcc.forEach((arr, uuid) => {
    const uniq = [...new Set(arr)].filter(Boolean).sort((a, b) => a.localeCompare(b));
    actuatorDisplayLabelByJointUuid.set(uuid, uniq.join(", "));
  });

  return { actuatedJointUuids, actuatorDisplayLabelByJointUuid };
}

export function getJointCssLabelMainText({
  showActuators,
  jointName,
  jointUuid,
  actuatorDisplayLabelByJointUuid,
}) {
  if (
    showActuators &&
    jointUuid &&
    actuatorDisplayLabelByJointUuid &&
    actuatorDisplayLabelByJointUuid.has(jointUuid)
  ) {
    const actLabel = actuatorDisplayLabelByJointUuid.get(jointUuid);
    if (actLabel) return actLabel;
  }
  return jointName ?? "";
}
